import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

/**
 * Identifies the required additional column that stores an entity's disposition
 * and the UDO instance to use when a new entity does not provide a value.
 *
 * The referenced additional column owns the UDO template configuration. Keeping
 * that relationship on the column avoids duplicating the UDO template ID here.
 */
export type OttopilotDispositionColumnConfig = {
    columnId: Uuid;
    defaultValue: Uuid;
};

export const ottopilotDispositionColumnConfigValidator: zod.ZodType<OttopilotDispositionColumnConfig> = zod.strictObject({
    columnId: uuidZodValidator,
    defaultValue: uuidZodValidator,
});

export function getOttopilotDispositionValue(
    entity: {additionalColumnValues: Readonly<Record<Uuid, unknown>>},
    configuration: OttopilotDispositionColumnConfig,
): Uuid {
    return uuidZodValidator.parse(entity.additionalColumnValues[configuration.columnId]);
}

export function getOptionalOttopilotDispositionValue(
    entity: {additionalColumnValues: Readonly<Record<Uuid, unknown>>},
    configuration: OttopilotDispositionColumnConfig,
): Uuid | null {
    const result = uuidZodValidator.safeParse(entity.additionalColumnValues[configuration.columnId]);
    return result.success === true ? result.data : null;
}
